/**
 * Attach rendering — the CRF Builder's pure helpers.
 *
 * The form card's "Attach rendering" takes a PDF or Word file, uploads it to
 * the rendering upload folder (getUploadUrlCore, `crfRenderingUploads`) and
 * asks attachCrfRenderingV2 to make it the form's page rendering. The server
 * decides what the file is from its bytes and converts Word; this module only
 * picks the upload's content type, which must match the one the upload
 * session was opened with.
 *
 * Tested from functions' vitest: web/src/pages/project/attachRendering.test.ts.
 */

/** What the file picker offers. */
export const ATTACH_RENDERING_ACCEPT =
  '.pdf,.docx,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document';

export interface RenderingSourceFields {
  sourceFilePath?: string;
  sourcePacketPath?: string;
  renderingSource?: 'user';
}

/** True when the form's rendering is a file the user attached. */
export function isUserRendering(form: RenderingSourceFields): boolean {
  return form.renderingSource === 'user' && Boolean(form.sourceFilePath);
}

/**
 * True when the form's own document stands and the platform must not render
 * a blank over it: an uploaded form, or one with a rendering the user
 * attached. Mirrors the server's guards (functions/src/crf/renderingSource.ts).
 */
export function keepsOwnDocument(form: RenderingSourceFields): boolean {
  return Boolean(form.sourcePacketPath) || isUserRendering(form);
}

/** The content type getUploadUrl opens the session with, from the file name. */
export function renderingUploadContentType(fileName: string): string | null {
  const lower = fileName.toLowerCase();
  if (lower.endsWith('.pdf')) return 'application/pdf';
  if (lower.endsWith('.docx')) return 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
  return null;
}

export function attachRenderingMessage(
  formName: string,
  result: { pageCount: number; converted: boolean },
): string {
  const pages = `${result.pageCount} page${result.pageCount === 1 ? '' : 's'}`;
  return `Rendering attached to ${formName}: ${pages}${result.converted ? ', converted from Word' : ''}.`;
}
